import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, DollarSign, Calendar, Clock, Globe, Save, Loader2,
  CheckCircle, ChevronLeft, Settings
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard } from '../components/Skeleton';
import BackButton from '../components/BackButton';

// ==========================================
// SETTING CARD COMPONENT
// ==========================================

const SettingCard = ({ title, description, icon: Icon, children, iconColor = 'purple' }) => (
  <div className="bg-white rounded-xl border p-6" data-testid={`setting-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-4 mb-4">
      <div className={`p-3 rounded-xl bg-${iconColor}-100`}>
        <Icon className={`w-5 h-5 text-${iconColor}-600`} />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================

const TenantSetupPage = () => {
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [settings, setSettings] = useState({
    currency_code: 'USD',
    date_format: 'YYYY-MM-DD',
    time_zone: 'UTC',
    fiscal_year_start: '01-01',
    default_working_hours: 40,
    default_working_days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
  });
  const [tenantInfo, setTenantInfo] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [currenciesRes, settingsRes] = await Promise.all([
        api.listCurrencies(),
        api.getTenantSettings(tenantId)
      ]);
      setCurrencies(currenciesRes.data.currencies || []);
      if (settingsRes.data.settings) {
        setSettings(settingsRes.data.settings);
      }
      setTenantInfo({
        name: settingsRes.data.tenant_name,
        id: settingsRes.data.tenant_id
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
      toast.error('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateTenantSettings(tenantId, settings);
      toast.success('Tenant settings saved');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleWorkingDayToggle = (day) => {
    setSettings(prev => ({
      ...prev,
      default_working_days: prev.default_working_days.includes(day)
        ? prev.default_working_days.filter(d => d !== day)
        : [...prev.default_working_days, day]
    }));
  };

  const WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  const DATE_FORMATS = [
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-03-11)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (11/03/2026)' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (03/11/2026)' },
    { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY (11-Mar-2026)' },
  ];

  const FISCAL_STARTS = [
    { value: '01-01', label: 'January (Calendar Year)' },
    { value: '04-01', label: 'April (UK, India)' },
    { value: '07-01', label: 'July (Australia)' },
    { value: '10-01', label: 'October (US Federal)' },
  ];

  const TIME_ZONES = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'Europe/London', label: 'London (UK)' },
    { value: 'Europe/Paris', label: 'Central European' },
    { value: 'Africa/Johannesburg', label: 'South Africa' },
    { value: 'Asia/Singapore', label: 'Singapore' },
    { value: 'Asia/Tokyo', label: 'Tokyo (Japan)' },
    { value: 'Australia/Sydney', label: 'Sydney (Australia)' },
  ];

  if (loading) {
    return (
      <PageErrorBoundary>
        <div className="min-h-screen bg-gray-50 p-6">
          <BackButton to="/admin" label="Back to Admin" className="mb-4" />
          <SkeletonCard />
          <div className="grid grid-cols-2 gap-6 mt-6">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </PageErrorBoundary>
    );
  }

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="tenant-setup-page">
        <BackButton to="/admin" label="Back to Admin" className="mb-4" />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tenant Setup</h1>
            <p className="text-gray-500">
              Configure tenant-wide settings for {tenantInfo?.name || 'your organization'}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Currency Setting */}
          <SettingCard
            title="Currency"
            description="Default currency for rates, costs, and financial reports"
            icon={DollarSign}
            iconColor="green"
          >
            <select
              value={settings.currency_code}
              onChange={(e) => setSettings(prev => ({ ...prev, currency_code: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="currency-select"
            >
              {currencies.map(curr => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} - {curr.name} ({curr.code})
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              This currency will be used across all resource rates, proposals, and financial views.
            </p>
          </SettingCard>

          {/* Date Format */}
          <SettingCard
            title="Date Format"
            description="How dates are displayed throughout the application"
            icon={Calendar}
            iconColor="blue"
          >
            <select
              value={settings.date_format}
              onChange={(e) => setSettings(prev => ({ ...prev, date_format: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="date-format-select"
            >
              {DATE_FORMATS.map(fmt => (
                <option key={fmt.value} value={fmt.value}>
                  {fmt.label}
                </option>
              ))}
            </select>
          </SettingCard>

          {/* Time Zone */}
          <SettingCard
            title="Time Zone"
            description="Default timezone for scheduling and timestamps"
            icon={Globe}
            iconColor="indigo"
          >
            <select
              value={settings.time_zone}
              onChange={(e) => setSettings(prev => ({ ...prev, time_zone: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="timezone-select"
            >
              {TIME_ZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </SettingCard>

          {/* Fiscal Year */}
          <SettingCard
            title="Fiscal Year Start"
            description="When your organization's fiscal year begins"
            icon={Calendar}
            iconColor="amber"
          >
            <select
              value={settings.fiscal_year_start}
              onChange={(e) => setSettings(prev => ({ ...prev, fiscal_year_start: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              data-testid="fiscal-year-select"
            >
              {FISCAL_STARTS.map(fy => (
                <option key={fy.value} value={fy.value}>
                  {fy.label}
                </option>
              ))}
            </select>
          </SettingCard>

          {/* Working Hours */}
          <SettingCard
            title="Default Working Hours"
            description="Standard weekly hours for capacity planning"
            icon={Clock}
            iconColor="purple"
          >
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={settings.default_working_hours}
                onChange={(e) => setSettings(prev => ({ ...prev, default_working_hours: parseFloat(e.target.value) || 40 }))}
                className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                min="0"
                max="168"
                data-testid="working-hours-input"
              />
              <span className="text-sm text-gray-500">hours per week</span>
            </div>
          </SettingCard>

          {/* Working Days */}
          <SettingCard
            title="Default Working Days"
            description="Standard working days for the organization"
            icon={Settings}
            iconColor="gray"
          >
            <div className="flex gap-2 flex-wrap">
              {WORKING_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => handleWorkingDayToggle(day)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    settings.default_working_days.includes(day)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`working-day-${day.toLowerCase()}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </SettingCard>
        </div>

        {/* Save Confirmation */}
        <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-purple-700">
              Settings apply tenant-wide and will affect all users.
            </span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save All Changes
          </button>
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default TenantSetupPage;
